import { forwardRef, Inject, Injectable } from '@angular/core';
import { DOCUMENT }           from '@angular/common';
import { TranslateService }   from '@ngx-translate/core';

import { ApiService }     from '../api/api.service';
import { CompanyService } from '../companies/company.service';
import { SessionService } from '../sessions/session.service';
import { UserModel }      from '../users/models/user';

@Injectable({
  providedIn: 'root'
})
export class BusinessIntelligenceService {
  dashboardId  = '';
  frame        = undefined;
  frameElement = undefined;
  token        = '';

  config = {
    dimensions: {
      date: '[DimDates.Date (Calendar)]'
    },
    endpoint: null
  };

  settings = {
    showLeftPane : false,
    showRightPane: false,
    showToolbar  : false
  };

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService      : ApiService,
    @Inject(forwardRef(() => CompanyService))
    private companyService  : CompanyService,
    @Inject(DOCUMENT)
    private document        : Document,
    @Inject(forwardRef(() => SessionService))
    private sessionService  : SessionService,
    private translateService: TranslateService
  ) {}

  addScript () : Promise<any> {
    return new Promise(( resolve, reject ) => {
      const doc    = this.document;
      const script = doc.createElement('script');

      script.setAttribute('src', `${ this.config.endpoint }/jwt?jwt=${ this.token }&return_to=/js/frame.js`);

      script.addEventListener('load', () => {
        resolve(window[ 'sisense.embed' ].SisenseFrame);

        script.removeAllListeners('load');
      });

      script.addEventListener('error', ( evt ) => {
        reject(evt);

        script.removeAllListeners('error');
      });

      doc.body.appendChild(script);
    });
  }

  bootstrapFrame ( element : Element ) : Promise<any> {
    this.frameElement = element;

    element.addEventListener('remove', evt => {
      this.logout();
    }, {
      once: true
    });

    return this.loadDependencies();
  }

  clearFilters () : Promise<any> {
    return this.frame.dashboard.removeFilters([{
      jaql: {
        dim: this.config.dimensions.date
      }
    }]);
  }

  filter ( filters : any = {} ) : Promise<any> {
    const jaql = {
      title   : 'Start/End Date',
      dim     : this.config.dimensions.date,
      datatype: 'datetime',
      level   : 'days',
      filter  : {
        from: filters.start_date,
        to  : filters.end_date
      }
    };

    return this.frame.dashboard.applyFilters([{ jaql }], false);
  }

  getBusinessIntelligenceEndpoint () : Promise<string> {
    return this.apiService.get('/business_intelligence/get_business_intelligence_url')
    .then(( endpoint ) => endpoint.business_intelligence_url);
  }

  getDashboards () : Promise<any> {
    return this.apiService.get('/business_intelligence/get_dashboard_list');
  }

  hideFrame () : Promise<any> {
    if ( this.frame ) {
      this.frame.hide();
    }

    return Promise.resolve(true);
  }

  isAvailable () : boolean {
    const session = this.sessionService.base();

    return session.gold_or_higher
      && ( session.subscription_status === 'trialing'
        || session.subscription_status === 'active' );
  }

  isReady () : Promise<any> {
    return this.companyService.hasIntegration('business-intelligence')
      ? this.apiService.get('/business_intelligence/business_intelligence_setup_check')
        .then(result => {
          if ( result === 'true' ) {
            return Promise.resolve({
              message: '',
              state  : 'ready'
            });
          }

          return result === 'failed'
            ? Promise.reject({
                message: this.translateService.instant('DASHBOARD_SPACE.BUSINESS_INTELLIGENCE.SETUP_FAILED'),
                state  : 'failed'
              })
            : Promise.reject({
                message: this.translateService.instant('DASHBOARD_SPACE.BUSINESS_INTELLIGENCE.SETUP_PENDING'),
                state  : 'pending'
              });
        })
      : Promise.reject({
        message: this.translateService.instant('DASHBOARD_SPACE.BUSINESS_INTELLIGENCE.SETUP_FAILED'),
        state  : 'failed'
      });
  }

  isTabBusinessIntelligence ( tab : any = {} ) : boolean {
    return tab.id === 'bi-customers'
      || tab.id === 'bi-overview'
      || tab.id === 'bi-products'
      || tab.id === 'bi-snapshot'
      || tab.id === 'bi-vehicles';
  }

  loadDashboard ( id : string ) : Promise<any> {
    this.dashboardId = id;

    if ( this.frame ) {
      return this.frame.dashboard.getCurrent()
      .then(( dashboard ) => {
        return dashboard.oid === this.dashboardId
          ? this.showFrame()
          : this.frame.dashboard.open(this.dashboardId)
            .then(() => this.showFrame());
      });
    }

    this.frame = new window[ 'sisense.embed' ].SisenseFrame({
      dashboard: this.dashboardId,
      element  : this.frameElement,
      settings : this.settings,
      url      : this.config.endpoint
    });

    return this.frame.render();
  }

  loadDependencies () : Promise<any> {
    return this.getBusinessIntelligenceEndpoint()
    .then(( endpoint : string ) => {
      this.config.endpoint = endpoint;

      return this.token
        ? Promise.resolve(true)
        : this.login();
    })
    .then(() => window[ 'sisense.embed' ]
      ? Promise.resolve(window[ 'sisense.embed' ].SisenseFrame)
      : this.addScript());
  }

  login () : Promise<any> {
    return this.token
      ? Promise.resolve(this.token)
      : this.apiService.get('/business_intelligence/login')
        .then(response => {
          this.token = response.jwt;

          return this.token;
        });
  }

  logout () : Promise<any> {
    if ( this.frame ) {
      this.frame = undefined;
    }

    return Promise.resolve(true);
  }

  print () : Promise<any> {
    return this.frame.dashboard.export('pdf');
  }

  showFrame () : Promise<any> {
    if ( this.frame ) {
      this.frame.show();
    }

    return Promise.resolve(true);
  }

  toggleAdvancedFilters () : Promise<any> {
    this.settings.showRightPane = !this.settings.showRightPane;

    return this.frame.updateSettings(this.settings);
  }

  updateStatus ( user : UserModel = {} ) : Promise<UserModel> {
    return this.apiService.get('/users/update_business_intelligence_status', [
      user.business_intelligence_user,
      user.id
    ])
    .then(() => user);
  }
}
